import { type PreviewModes, PurcsPreview } from '@vp/react-product-preview';
import { Button, Card, FluidImage } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { useLineItemData } from '../../contexts/LineItemDataContext';

interface ProductImagePreviewProps {
  previewMode: PreviewModes;
  onClick?: () => void;
}

export const ProductImagePreview = ({
  previewMode,
  onClick,
}: ProductImagePreviewProps) => {
  const [lineItemMessages = {}] = useTranslations(['lineItemMessages']);

  const {
    livePreviewUrl,
    documentUrl: documentRefUrl,
    attributes: productAttributeList,
    productKey,
    productVersion,
    productName,
  } = useLineItemData();

  // PURCs required params: https://gitlab.com/vistaprint-org/design-technology/scenes/purcs-react#required-parameters
  const hasRequiredPurcsData = !!(
    productKey &&
    productAttributeList &&
    documentRefUrl
  );

  if (hasRequiredPurcsData) {
    /**
     * Extracts the key and value for each product attribute
     *
     * Before: an array of product attribute objects
     * ex)
     * [
     *  {
     *   key: 'Product Orientation',
     *   value: 'Horizontal',
     *   keyDisplayName: null,
     *   valueDisplayName: null,
     *  },
     * ];
     *
     * After: an object mapping each product attribute key to its value
     * ex)
     * { Product Orientation: 'Horizontal' }
     *
     */
    const optionSelections = productAttributeList.reduce((acc, val) => {
      const { key, value } = val;
      return {
        ...acc,
        [key]: value,
      };
    }, {});

    return (
      <PurcsPreview
        requestor="orders.orderHistory"
        coreProductKey={productKey}
        productVersion={productVersion || undefined}
        docRefURL={documentRefUrl}
        optionSelections={optionSelections}
        imageWrapper={({ children }) => {
          if (!onClick) {
            return children;
          }

          return (
            <Button
              skin="unstyled"
              onClick={(e) => {
                e.preventDefault();
                onClick();
              }}
            >
              {children}
            </Button>
          );
        }}
        previewMode={previewMode}
        accessibleTextForNext={
          lineItemMessages.previewProductAccessibleTextForNext
        }
        accessibleTextForPrevious={
          lineItemMessages.previewProductAccessibleTextForPrevious
        }
        productName={productName || undefined}
        previewInstructionsUri={undefined} // null is default but doesn't work and is required?
        width={1000} // 1000 is default but is required?
      />
    );
  }

  if (livePreviewUrl) {
    return (
      <Card backgroundColor="strong" overflow="hidden">
        <FluidImage
          src={livePreviewUrl}
          alt={productName || undefined}
          onClick={onClick}
        />
      </Card>
    );
  }

  return null;
};
